/* eslint-disable no-bitwise */
import {
	Box,
	Center,
	createStyles,
	Group,
	Stack,
	useMantineTheme,
} from '@mantine/core';
import type { LiteIntegration } from '@repo/api-codegen';
import { EmptyState } from '@repo/common/components';
import { Button, CopyButton, Title } from '@repo/foundations';
import dayjs from 'dayjs';
import { countBy } from 'lodash-es';
import * as monaco from 'monaco-editor';
import { LanguageIdEnum, setupLanguageFeatures } from 'monaco-sql-languages';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSuperuserQueryExecution } from '../../../api/hooks/metric/useMetricExecution';
import { MetricJobProgress } from '../../../components/BackgroundJobProgress/MetricJobProgress';
import { DataDisplayTable } from '../../../components/DataDisplayTable';
import ErrorDrawer from '../../../components/ErrorDrawer/ErrorDrawer';
import { ErrorAction } from '../../../components/Sql/Actions/ErrorAction';
import { RunAction } from '../../../components/Sql/Actions/RunAction';
import {
	completionService,
	internalDatabaseCompletionService,
} from '../../../components/Sql/SqlEditor/languageSetup';
import { getEditorOptions } from '../../../components/Sql/SqlEditor/SqlEditor.helpers';
import { getMonacoTheme } from '../../../components/Sql/SqlEditor/SqlEditorTheme/monacoTheme';
import { hashCode } from '../../../utils/utils';

export interface InspectSQLBlockProps {
	integration?: LiteIntegration;
	onDelete: () => void;
}

const useStyles = createStyles((theme) => ({
	wrapper: {
		padding: theme.spacing.md,
		minHeight: 300,
	},
	editor: {
		height: '100%',
		// adding multiple editors causes each new one to add padding to width,
		// so we need to subtract the padding off the width for each new editor
		width: `calc(100% - ${theme.spacing.md})`,
		minHeight: 280,
		border: 'solid',
		borderRadius: theme.spacing.md,
		padding: theme.spacing.md,
	},
	results: {
		width: '100%',
		height: '100%',
		overflowX: 'auto',
		maxWidth: '90vw',
	},
	warningBox: {
		border: 'solid',
		borderWidth: 0.5,
		borderRadius: theme.radius.md,
		borderColor: theme.other.getColor('fill/transparent/active'),
		boxShadow: theme.shadows.md,
		padding: theme.spacing.sm,
	},
	warningIcon: {
		backgroundColor: theme.other.getColor('fill/caution/default'),
		borderRadius: theme.radius.sm,
		padding: theme.spacing['3xs'],
	},
}));

export default function InspectSQLBlock({
	integration,
	onDelete,
}: InspectSQLBlockProps) {
	const theme = useMantineTheme();
	const hostRef = useRef<HTMLDivElement>(null);
	const editorRef = useRef<monaco.editor.IStandaloneCodeEditor>();

	const { classes } = useStyles();
	const [query, setQuery] = useState<string>('');

	const handleDelete = () => {
		onDelete();
	};

	// Run query
	const { execute, executionStatus, isExecuting } =
		useSuperuserQueryExecution();
	const handleExecute = useCallback(async () => {
		const currentQuery = editorRef.current?.getValue() ?? query;
		await execute(currentQuery, integration?.id);
	}, [execute, integration?.id, query]);
	const [showErrorDrawer, setShowErrorDrawer] = useState(false);

	// Results
	const results = executionStatus?.results ?? [];
	const key = hashCode(JSON.stringify(results));

	// Render editor
	useEffect(() => {
		if (hostRef.current && !editorRef.current) {
			setupLanguageFeatures(LanguageIdEnum.MYSQL, {
				completionItems: {
					enable: true,
					completionService: integration?.id
						? completionService(integration?.id, integration?.type || undefined)
						: internalDatabaseCompletionService(),
				},
			});

			monaco.editor.defineTheme('sqlTheme', getMonacoTheme(theme));
			editorRef.current = monaco.editor.create(hostRef.current, {
				language: LanguageIdEnum.MYSQL,
				theme: 'sqlTheme',
				...getEditorOptions(true, false, false),
				glyphMargin: countBy(query)?.['\n'] > 20,
				value: query,
			});
			editorRef.current.onDidChangeModelContent(() => {
				const value = editorRef?.current?.getValue() ?? '';
				setQuery(value);
			});
			editorRef.current.addAction({
				id: 'runQuery',
				label: 'Run Query',
				precondition: 'editorTextFocus',
				keybindings: [
					monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter,
					monaco.KeyMod.WinCtrl | monaco.KeyCode.Enter,
					monaco.KeyMod.Shift | monaco.KeyCode.Enter,
				],
				run: () => {
					handleExecute();
				},
			});
		}
	}, [integration?.id, integration?.type, handleExecute, query, theme]);

	return (
		<Box className={classes.wrapper}>
			<Group position="apart" w="100%" mb="md">
				<Button onClick={handleDelete} leftIconName="trash" size="md">
					Delete block
				</Button>
				<Group>
					<ErrorAction
						disabled={executionStatus?.failed !== true}
						handleShow={() => setShowErrorDrawer(true)}
					/>
					<MetricJobProgress
						isExecuting={isExecuting}
						logs={executionStatus?.logs || undefined}
						timeLimit={-1}
					/>
					<RunAction disabled={false} handleExecute={handleExecute} />
				</Group>
			</Group>
			<div ref={hostRef} className={classes.editor} />
			<Box key={key} className={classes.results}>
				<Title order={5} py="md">
					Results
				</Title>
				{/* We check for results.length <= 1 because the first row is the column names. */}
				{results.length <= 1 && (
					<Center data-testid="sql-editor-preview-empty-state" py={60}>
						<EmptyState
							size="lg"
							iconName="tableRow"
							title="No data found"
							description="This query returned no rows. Try adjusting the query"
							includeGoBack={false}
						/>
					</Center>
				)}
				{results.length > 0 && (
					<Stack>
						<DataDisplayTable
							results={results as unknown as string[][]}
							resizable
						/>
						<Group w="100%" align="right">
							<CopyButton value={JSON.stringify(results, null, 2)} />
						</Group>
					</Stack>
				)}
			</Box>
			{executionStatus?.failed === true && (
				<ErrorDrawer
					title={/\(.*errors.(.*)\)/.exec(executionStatus?.logs || '')?.[1]}
					errorMessage={executionStatus?.logs || ''}
					open={showErrorDrawer}
					onClose={() => setShowErrorDrawer(false)}
					errorAt={dayjs().toISOString()}
				/>
			)}
		</Box>
	);
}
